import moment from "moment";
import { ReservationEvent, Reservation, ReservationType } from "src/types";

// Convert reservations to calendar events
export const createScheduleEvents = (reservations: Reservation[], currentDate: Date, types: ReservationType[]): ReservationEvent[] => {  
  // On mobile, when looking at a month, we use agenda view, which shouldn't have extra days shown
  const calendarBufferDays = 7;

  // Calculate view range (start and end dates)
  const viewStartDate = moment(currentDate).startOf('month').subtract(calendarBufferDays, 'days').format('YYYY-MM-DD');
  const viewEndDate = moment(currentDate).endOf('month').add(calendarBufferDays, 'days').format('YYYY-MM-DD');

  // Process all reservations (regular and recurring)
  let allEvents: Reservation[] = [];

  reservations.forEach((reservation: Reservation) => {
    // For recurring events, generate all occurrences within the view range
    if (reservation.recurring_frequency) {
      const recurringEvents = generateRecurringEvents(
        reservation,
        viewStartDate,
        viewEndDate
      );
      allEvents = [...allEvents, ...recurringEvents];
    } else {
      // For regular events, just add the reservation as is
      allEvents.push(reservation);
    }
  });

  // Convert all events to calendar format
  return allEvents.map(reservation => {          

    // Format start and end times
    const startDate = moment(`${reservation.start_date} ${reservation.start_time}`).toDate();
    const endDate = moment(`${reservation.end_date} ${reservation.end_time}`).toDate();

    // Create title with recurring indicator
    let title = reservation.title || reservation.description || ''; 

    const type = reservation.type_id ? types.find(type => type.id === reservation.type_id) : null;
    
    return {
      startDate: startDate,
      endDate: endDate,
      startTime: reservation.start_time,
      endTime: reservation.end_time,
      title: title,
      opacity: 1,
      hexColor: (type ? type.color : "white") || "white",
      reservation: reservation.originalReservation || reservation, // Keep the original reservation for recurring events
      isRecurring: !!reservation.recurring_frequency || !!reservation.isRecurring,
      type: reservation.type_id,
      allDay: false,
    };
  });
};

// Generate recurring events based on frequency and date range
const generateRecurringEvents = (
  reservation: Reservation, 
  startOfViewRange: string, 
  endOfViewRange: string
): Reservation[] => {
  const events: Reservation[] = [];
  const frequency = reservation.recurring_frequency;
  
  if (!frequency) {
    return events;
  }

  // Get the recurring gap, default to 1 if not specified
  const recurringGap = reservation.recurring_gap && reservation.recurring_gap > 0 
    ? Math.floor(reservation.recurring_gap) : 1;

  // Parse original start date and time
  const originalStartDate = moment(`${reservation.start_date} ${reservation.start_time}`);
  
  // Handle end date - if falsy, event goes on forever
  const hasEndDate = reservation.end_date && reservation.end_time;
  const originalEndDate = hasEndDate 
    ? moment(`${reservation.end_date} ${reservation.end_time}`)
    : null;
  
  // Calculate appropriate start and end dates for the view
  const viewStart = moment(startOfViewRange).startOf('day');
  const viewEnd = moment(endOfViewRange).endOf('day');
  
  // For monthly/yearly with week_number and day_of_week, we need to calculate differently
  if (frequency === 'monthly' || frequency === 'yearly') {
    if (reservation.week_number && reservation.day_of_week && !reservation.recurring_date) {
      // Start from the appropriate month
      let currentMonth = frequency === 'monthly'
        ? moment(originalStartDate).startOf('month')
        : moment(originalStartDate).month(reservation.recurring_month ? reservation.recurring_month - 1 : originalStartDate.month()).add(reservation.recurring_month && originalStartDate.month() >= reservation.recurring_month ? 1 : 0, 'year').startOf('month');
      
      // If original date is before the view range, advance to appropriate month
      if (currentMonth.isBefore(viewStart, 'month')) {
        if (frequency === 'monthly') {
          // Calculate how many gap periods to advance
          const monthsDiff = viewStart.diff(currentMonth, 'months');
          const periodsToAdvance = Math.floor(monthsDiff / recurringGap);
          currentMonth.add(periodsToAdvance * recurringGap, 'months');
        } else if (frequency === 'yearly') {
          // For yearly, advance to the correct year and month
          const targetYear = viewStart.year();
          const targetMonth = reservation.recurring_month ? reservation.recurring_month - 1 : originalStartDate.month();
          currentMonth = moment().year(targetYear).month(targetMonth).startOf('month');
          
          // If we're still before the view, advance one more year
          if (currentMonth.isBefore(viewStart, 'month')) {
            currentMonth.add(1, 'year');
          }
        }
      }
      
      // Generate events
      while (currentMonth.isSameOrBefore(viewEnd, frequency === 'monthly' ? 'month' : 'year')) {
        try {
          // For yearly events, ensure we're in the correct month
          if (frequency === 'yearly' && reservation.recurring_month) {
            currentMonth.month(reservation.recurring_month - 1);
          }
          
          const targetDate = findDateInMonth(currentMonth, reservation.week_number, reservation.day_of_week);
          
          // Check if this specific event date is within bounds
          const isWithinViewRange = targetDate.isSameOrAfter(viewStart, 'day') && targetDate.isSameOrBefore(viewEnd, 'day');
          const isWithinEndDate = !hasEndDate || !originalEndDate || targetDate.isSameOrBefore(originalEndDate, 'day');
          const isAfterOriginalStart = targetDate.isSameOrAfter(originalStartDate, 'day');
          
          if (isWithinViewRange && isWithinEndDate && isAfterOriginalStart) {
            events.push({
              ...reservation,
              start_date: targetDate.format('YYYY-MM-DD'),
              end_date: targetDate.format('YYYY-MM-DD'),
              start_time: originalStartDate.format('HH:mm'),
              end_time: (!!reservation.end_time && moment(reservation.end_time, ['HH:mm:ss', 'HH:mm'], true).isValid())
                ? moment(reservation.end_time, ['HH:mm:ss', 'HH:mm'], true).format('HH:mm')
                : originalStartDate.format('HH:mm'),
              isRecurring: true,
              originalReservation: reservation
            });
          }
        } catch (error) {
          // Skip months where the target week/day doesn't exist
          console.warn(`Skipping month ${currentMonth.format('YYYY-MM')} - target date doesn't exist:`, error);
        }
        
        // Advance to next occurrence
        if (frequency === 'monthly') {
          currentMonth.add(recurringGap, 'months');
        } else if (frequency === 'yearly') {
          currentMonth.add(1, 'year');
        }
      }
      
      return events;
    } else if (reservation.recurring_date) {
      // Handle specific recurring date logic, such as "always January 1st" or "every 15th of the month"
      let currentMonth = frequency === 'monthly'
        ? moment(originalStartDate).startOf('month')
        : moment(originalStartDate).month(reservation.recurring_month ? reservation.recurring_month - 1 : originalStartDate.month()).add(reservation.recurring_month && originalStartDate.month() >= reservation.recurring_month ? 1 : 0, 'year').startOf('month');
      
      // If original date is before the view range, advance to appropriate month
      if (currentMonth.isBefore(viewStart, 'month')) {
        if (frequency === 'monthly') {
          const monthsDiff = viewStart.diff(currentMonth, 'months');
          const periodsToAdvance = Math.floor(monthsDiff / recurringGap);
          currentMonth.add(periodsToAdvance * recurringGap, 'months');
        } else if (frequency === 'yearly') {
          const targetYear = viewStart.year();
          const targetMonth = reservation.recurring_month ? reservation.recurring_month - 1 : originalStartDate.month();
          currentMonth = moment().year(targetYear).month(targetMonth).startOf('month');
          
          if (currentMonth.isBefore(viewStart, 'month')) {
            currentMonth.add(1, 'year');
          }
        }
      }
      
      // Generate events using the specific date
      while (currentMonth.isSameOrBefore(viewEnd, frequency === 'monthly' ? 'month' : 'year')) {
        // For yearly events, ensure we're in the correct month
        if (frequency === 'yearly' && reservation.recurring_month) {
          currentMonth.month(reservation.recurring_month - 1);
        }
        
        // Use the recurring_date as the day of the month
        const targetDate = currentMonth.clone().date(reservation.recurring_date);
        
        // Check if the date is valid (e.g., February 30th doesn't exist)
        if (targetDate.date() === reservation.recurring_date) {
          // Check if this specific event date is within bounds
          const isWithinViewRange = targetDate.isSameOrAfter(viewStart, 'day') && targetDate.isSameOrBefore(viewEnd, 'day');
          const isWithinEndDate = !hasEndDate || !originalEndDate || targetDate.isSameOrBefore(originalEndDate, 'day');
          const isAfterOriginalStart = targetDate.isSameOrAfter(originalStartDate, 'day');
          
          if (isWithinViewRange && isWithinEndDate && isAfterOriginalStart) {
            events.push({
              ...reservation,
              start_date: targetDate.format('YYYY-MM-DD'),
              end_date: targetDate.format('YYYY-MM-DD'),
              start_time: originalStartDate.format('HH:mm'),
              end_time: (!!reservation.end_time && moment(reservation.end_time, ['HH:mm:ss', 'HH:mm'], true).isValid())
                ? moment(reservation.end_time, ['HH:mm:ss', 'HH:mm'], true).format('HH:mm')
                : originalStartDate.format('HH:mm'),
              isRecurring: true,
              originalReservation: reservation
            });
          }
        }
        
        // Advance to next occurrence
        if (frequency === 'monthly') {
          currentMonth.add(recurringGap, 'months');
        } else if (frequency === 'yearly') {
          currentMonth.add(1, 'year');
        }
      }
      
      return events;
    }
  }
  
  // Original logic for other frequency types or when using recurring_date
  let currentDate = moment(originalStartDate);
  
  // If original date is before the view range, advance to the start of view
  if (currentDate.isBefore(viewStart)) {
    const dayOfMonth = originalStartDate.date(); // Get date (1-31)
    const month = originalStartDate.month(); // 0-11

    // Calculate the next occurrence based on frequency
    switch (frequency) {
      case 'daily':
        // Advance by days until we reach viewStart, accounting for gap
        while (currentDate.isBefore(viewStart)) {
          currentDate.add(recurringGap, 'day');
        }
        break;
        
      case 'weekly':
        // Advance by weeks until we reach viewStart
        while (currentDate.isBefore(viewStart)) {
          currentDate.add(recurringGap, 'week');
        }
        break;

      case 'monthly':          
        // Advance by months until we reach viewStart
        while (currentDate.isBefore(viewStart)) {
          currentDate.add(recurringGap, 'month');

          // Adjust for months with fewer days (e.g. Going from January 31 to February 28)
          const lastDayOfMonth = currentDate.daysInMonth();
          if (dayOfMonth > lastDayOfMonth) {
            currentDate.date(lastDayOfMonth);
          } else {
            currentDate.date(dayOfMonth);
          }
        }
        break;
        
      case 'yearly':          
        // Advance by years until we reach viewStart
        while (currentDate.isBefore(viewStart)) {
          currentDate.add(1, 'year');
          
          // Handle February 29 in non-leap years
          if (month === 1 && dayOfMonth === 29 && !moment([currentDate.year()]).isLeapYear()) {
            currentDate.month(month).date(28);
          } else {
            currentDate.month(month).date(dayOfMonth);
          }
        }
        break;
    }
  }
  
  // Generate events until end of view range (or original end date if it exists)
  const shouldContinue = (date: moment.Moment): boolean => {
    if (date.isSameOrAfter(viewEnd)) return false;
    if (hasEndDate && originalEndDate && date.isAfter(originalEndDate)) return false;
    return true;
  };
  
  while (shouldContinue(currentDate)) {
    if (frequency === 'weekly' && reservation.days_of_week && reservation.days_of_week.length > 0) {
      // Handle multiple days of the week
      const currentDayOfWeek = currentDate.isoWeekday(); // 1 = Monday, 7 = Sunday
      
      for (const targetDayOfWeek of reservation.days_of_week) {
        // Calculate days to add (can be negative for earlier in week, or positive for later/next week)
        let daysToAdd = ((targetDayOfWeek - currentDayOfWeek)+7) % 7; // Wrap around to next week if needed
        
        const eventDate = moment(currentDate).add(daysToAdd, 'days');
        
        // Check if this specific event date is within bounds
        const isWithinViewRange = eventDate.isBefore(viewEnd);
        const isWithinEndDate = !hasEndDate || !originalEndDate || eventDate.isSameOrBefore(originalEndDate);
        
        if (isWithinViewRange && isWithinEndDate) {
          const eventStartDate = moment(eventDate);
          const eventEndDate = moment(eventDate); // all recurring events are not multi-day
          
          events.push({
            ...reservation,
            start_date: eventStartDate.format('YYYY-MM-DD'),
            end_date: eventEndDate.format('YYYY-MM-DD'),
            start_time: originalStartDate.format('HH:mm'),
            end_time: (!!reservation.end_time && moment(reservation.end_time, ['HH:mm:ss', 'HH:mm'], true).isValid())
              ? moment(reservation.end_time, ['HH:mm:ss', 'HH:mm'], true).format('HH:mm')
              : originalStartDate.format('HH:mm'),
            isRecurring: true,
            originalReservation: reservation
          });
        }
      }
    } else {
      // Handle single day (original logic for non-weekly or weekly without days_of_week)
      const eventStartDate = moment(currentDate);
      const eventEndDate = moment(currentDate); // all recurring events are not multi-day
      
      events.push({
        ...reservation,
        start_date: eventStartDate.format('YYYY-MM-DD'),
        end_date: eventEndDate.format('YYYY-MM-DD'),
        start_time: originalStartDate.format('HH:mm'),
        end_time: (!!reservation.end_time && moment(reservation.end_time, ['HH:mm:ss', 'HH:mm'], true).isValid())
          ? moment(reservation.end_time, ['HH:mm:ss', 'HH:mm'], true).format('HH:mm')
          : originalStartDate.format('HH:mm'),
        isRecurring: true,
        originalReservation: reservation
      });
    }

    let dayOfMonth;
    let lastDayOfMonth;

    // Advance to next occurrence
    switch (frequency) {
      case 'daily':
        currentDate.add(recurringGap, 'day');
        break;
        
      case 'weekly':
        currentDate.add(recurringGap, 'week');
        break;
        
      case 'monthly':
        // Add a month
        currentDate.add(1, 'month');
        dayOfMonth = originalStartDate.date();
        lastDayOfMonth = currentDate.daysInMonth();

        // Adjust for months with fewer days
        if (dayOfMonth > lastDayOfMonth) {
          currentDate.date(lastDayOfMonth);
        } else {
          currentDate.date(dayOfMonth);
        }
        break;
        
      case 'yearly':
        currentDate.add(1, 'year');
        break;
    }
  }
  
  return events;
};

const findDateInMonth = (
  monthStart: moment.Moment, 
  weekNumber: number, 
  dayOfWeek: number
): moment.Moment => {
  const month = monthStart.clone();
  
  if (weekNumber === -1) {
    // Last occurrence of dayOfWeek in the month
    const lastDayOfMonth = month.clone().endOf('month');
    const lastWeekday = lastDayOfMonth.clone();
    
    // Move backwards to find the last occurrence of the target day
    while (lastWeekday.isoWeekday() !== dayOfWeek) {
      lastWeekday.subtract(1, 'day');
    }
    
    return lastWeekday;
  } else {
    // Nth occurrence of dayOfWeek in the month (1st, 2nd, 3rd, 4th)
    const firstDayOfMonth = month.clone().startOf('month');
    let targetDate = firstDayOfMonth.clone();
    
    // Find the first occurrence of the target day
    while (targetDate.isoWeekday() !== dayOfWeek) {
      targetDate.add(1, 'day');
    }
    
    // Add weeks to get to the Nth occurrence
    targetDate.add((weekNumber - 1) * 7, 'days');
    
    // Check if we're still in the same month
    if (targetDate.month() !== month.month()) {
      throw new Error(`Week ${weekNumber} of day ${dayOfWeek} does not exist in ${month.format('YYYY-MM')}`);
    }
    
    return targetDate;
  }
};